import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/hooks/useAuth";
import { AuthForm } from "@/components/AuthForm";
import { LogOut, PanelTopOpen, Search } from "lucide-react";
import BrandText from "@/components/brand/BrandText";
import { BrandSwitcher } from "@/components/brand/BrandSwitcher";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { NotificacoesBadge } from "@/components/NotificacoesBadge";
import { useAtualizarUltimoAcesso } from "@/hooks/useAtualizarUltimoAcesso";
import { FloatingDiscadoraButton } from "@/components/discadora/FloatingDiscadoraButton";
import { NotificacaoLateral } from "@/components/NotificacaoLateral";
import { SoftphoneProvider } from "@/components/softphone/SoftphoneProvider";
import { FloatingSoftphone } from "@/components/softphone/FloatingSoftphone";
import { useNotificacaoFatura } from "@/hooks/useNotificacaoFatura";
import { useAvisoTurnoInstalacao } from "@/hooks/useAvisoTurnoInstalacao";
import { useNotificacaoCliqueEmail } from "@/hooks/useNotificacaoCliqueEmail";
import { forceCleanLogout } from "@/lib/authRecovery";
import { useNotificacaoDuvidaSupervisora } from "@/hooks/useNotificacaoDuvidaSupervisora";
import { useNotificacaoNota1Gerente } from "@/hooks/useNotificacaoNota1Gerente";
import { BkoPortabilidadeChipsInline } from "@/components/layout/BkoPortabilidadeBanner";
import { AprovacoesChipInline } from "@/components/layout/AprovacoesChip";
import { PedirHoraExtraInline } from "@/components/jornada/PedirHoraExtra";
import { WhatsAppInboxChips } from "@/components/layout/WhatsAppInboxChips";
import { DiscadoraFichaGlobal } from "@/components/discadora/DiscadoraFichaGlobal";
import { BkoContratoPrimeiroACriacao } from "@/components/kanban/contrato/BkoContratoPrimeiroACriacao";
import { BkoTramitacaoTrava } from "@/components/kanban/tramitacao/BkoTramitacaoTrava";
import { BkoPortabilidadeTrava } from "@/components/kanban/ativacao/BkoPortabilidadeTrava";
import { QualidadeTrava } from "@/components/qualidade/QualidadeTrava";
import { AvisoVenceEm30 } from "@/components/sla/AvisoVenceEm30";
import { RecadoDaSupervisora } from "@/components/backoffice/RecadoDaSupervisora";
import { abrirMesaDiscadora } from "@/lib/discadoraMesa";
import { useShellScreenTitle } from "@/components/layout/ShellTabs";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  useCommandPalette,
  useCommandPaletteShortcut,
} from "@/hooks/use-command-palette";
import { cn } from "@/lib/utils";

// 🚀 Preload das páginas mais acessadas após login
const preloadPages = () => {
  setTimeout(() => {
    import("@/pages/Dashboards");
    import("@/pages/Planilha");
    import("@/pages/Kanbans");
    import("@/pages/Comercial");
  }, 1000);
};

export function Layout() {
  const { user, loading, signOut, profile } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Detecta rotas que usam layout com scroll interno (tabelas full-height)
  const OVERFLOW_HIDDEN_ROUTES = ["/comercial", "/discadora", "/whatsapp-inbox", "/rh", "/conexoes-whatsapp"];
  const isVisaoPrime =
    (location.pathname === "/dashboards" && searchParams.get("tab") === "visao-prime") ||
    // Ranking (rodadas 81/82): a tela cabe em 803px e não rola.
    (location.pathname === "/dashboards" && (searchParams.get("tab") ?? "gerencial") === "gerencial") ||
    OVERFLOW_HIDDEN_ROUTES.includes(location.pathname);
  
  // Hook para atualizar último acesso do usuário (para distribuição BKO)
  useAtualizarUltimoAcesso();
  
  // Hook para notificar quando fatura estiver pronta
  useNotificacaoFatura();

  // Fibra em instalação agora: o turno abriu (Douglas 30/10/2026)
  useAvisoTurnoInstalacao();

  useNotificacaoCliqueEmail();

  // Hook para BKO Portabilidade — toast verde quando cliente responde BV/Fatura ou chama espontaneamente

  // Hook para Supervisora da Qualidade — toast roxo quando cliente aperta "Tenho dúvida"
  useNotificacaoDuvidaSupervisora();

  // Hook para Gerentes + Supervisor do consultor — toast vermelho quando nota_atendimento vira 1
  useNotificacaoNota1Gerente();

  // Atalho global Cmd+K / Ctrl+K abre Command Palette
  useCommandPaletteShortcut();
  const { setOpen: setCmdkOpen } = useCommandPalette();
  const screenTitle = useShellScreenTitle();
  const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC");

  // 🚀 Preload de páginas após login bem-sucedido
  useEffect(() => {
    if (user && profile) {
      preloadPages();
    }
  }, [user, profile]);

  const handleLogout = async () => {
    try {
      await signOut();
      toast({
        title: "Logout realizado",
        description: "Você saiu do sistema com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro ao sair",
        description: "Não foi possível fazer logout.",
        variant: "destructive",
      });
    }
  };

  const handleAbrirMesa = () => {
    const janela = abrirMesaDiscadora();
    if (!janela) {
      toast({
        title: "A Mesa não abriu",
        description: "Libere pop-ups deste CRM e clique novamente em Mesa.",
        variant: "destructive",
      });
      return;
    }
  };

  // Mostrar spinner APENAS durante verificação inicial de auth.
  // Se passar de 15s ainda em loading, é refresh token quebrado — força
  // logout limpo (limpa localStorage + redirect pra /auth com mensagem amigável).
  // Antes ficava preso em "Servidor temporariamente indisponível" + botão
  // "Tentar novamente" que só recarregava com o mesmo token podre, em loop.
  const [bootTimeout, setBootTimeout] = useState(false);

  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setBootTimeout(true), 15000);
      return () => clearTimeout(timer);
    }
    setBootTimeout(false);
  }, [loading]);

  // Quando bate o timeout, dispara recuperação automática.
  // Sai do componente — o usuário será redirecionado pra /auth.
  useEffect(() => {
    if (bootTimeout) {
      forceCleanLogout('boot_timeout');
    }
  }, [bootTimeout]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="h-8 w-8 animate-spin mx-auto border-4 border-primary border-t-transparent rounded-full" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Sem usuário = tela de login
  if (!user) {
    return <AuthForm />;
  }
  
  // Perfil carrega em background - NÃO bloqueia navegação

  return (
    <SoftphoneProvider enabled={!!user}>
    <SidebarProvider>
      <div className="flex h-screen w-screen overflow-hidden bg-background">
        <AppSidebar />
        
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Nos dashboards (TV) o header cede 6px pra tabela de equipes —
              rodada 4 do design: a janela real tem ~874px úteis, não 900. */}
          <header className={`shrink-0 ${location.pathname.startsWith('/dashboards') ? 'h-[46px]' : 'h-[52px]'} border-b border-border bg-surface px-4 flex items-center justify-between gap-4`}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <SidebarTrigger className="hover:bg-accent" />
              <BrandText className="hidden sm:inline-flex font-display text-[17px] font-bold tracking-[-0.2px] shrink-0 [&>span:first-child]:text-primary dark:[&>span:first-child]:text-white [&>span:last-child]:text-primary dark:[&>span:last-child]:text-brand-header" />
              {screenTitle && (
                <>
                  <div className="hidden md:block h-5 w-px bg-border shrink-0" />
                  <span className="hidden md:block text-[13px] font-bold text-foreground truncate">
                    {screenTitle}
                  </span>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Chips de trabalho — ao lado do sino, estilo sidebar */}
              <div className="hidden md:flex h-8 items-center mr-1 min-w-0 rounded-[9px] border border-border bg-surface shadow-[0_1px_2px_rgba(60,20,30,0.05)] overflow-hidden">
                <AprovacoesChipInline />
                <PedirHoraExtraInline />
                <BkoPortabilidadeChipsInline />
                <WhatsAppInboxChips />
              </div>
              <button
                type="button"
                onClick={handleAbrirMesa}
                className="hidden h-8 flex-shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 text-[11px] font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary md:inline-flex"
                title="Abrir a Mesa da Discadora em uma janela separada"
              >
                <PanelTopOpen className="h-3.5 w-3.5 text-primary" />
                Mesa
              </button>
              {/* Atalho Cmd+K — abre Command Palette */}
              <button
                type="button"
                onClick={() => setCmdkOpen(true)}
                className="hidden md:inline-flex items-center gap-2 h-8 pl-2.5 pr-1.5 rounded-md border border-border bg-background/40 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/60 transition"
                aria-label="Abrir busca rápida"
                title="Buscar (⌘K)"
              >
                <Search className="h-3.5 w-3.5" />
                <span>Buscar</span>
                <kbd className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-border bg-muted/50 text-[10px] font-mono text-muted-foreground">
                  {isMac ? "⌘" : "Ctrl"}K
                </kbd>
              </button>
              <BrandSwitcher />
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface [&>*]:h-full [&>*]:w-full [&_button]:h-full [&_button]:w-full">
                <NotificacoesBadge />
              </span>
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface [&>*]:h-full [&>*]:w-full [&_button]:h-full [&_button]:w-full">
                <ThemeToggle />
              </span>
              {/* (Douglas 07/07) nome do usuário + Sair REMOVIDOS do topo — já
                  existem no rodapé da sidebar. Tira a duplicidade e libera espaço
                  no header pros chips/busca. */}
            </div>
          </header>

          <main className={cn(
            "flex-1 min-h-0",
            isVisaoPrime ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden"
          )}>
            <Outlet />
          </main>
        </div>
      </div>
      
      <FloatingDiscadoraButton />
      <NotificacaoLateral />
      {!!user && <FloatingSoftphone />}
      {!!user && <DiscadoraFichaGlobal />}
      {!!user && <BkoContratoPrimeiroACriacao />}
      {!!user && <BkoTramitacaoTrava />}
      {!!user && <BkoPortabilidadeTrava />}
      {!!user && <QualidadeTrava />}
      {!!user && <AvisoVenceEm30 />}
      {!!user && <RecadoDaSupervisora />}
    </SidebarProvider>
    </SoftphoneProvider>
  );
}
